// Lightweight tRPC client without code-generation. The referee app posts JSON
// to /api/trpc/<procedure> on the same origin (proxied to apps/web by nginx).
// This avoids pulling @trpc/client into the PWA bundle when only a handful of
// procedures are called.

import { tracked } from './telemetry';

const BASE = '/api/trpc';

/**
 * A failed API call. `message` is the server's German, user-readable text
 * (the API's errorFormatter); `code` is the error code (`lane_required`,
 * …) for branches; `procedure` is for logs only — never shown.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly procedure: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Error code of a failed call, or null. */
export function errorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

/**
 * The server couldn't be reached (no network, or a gateway answering for a
 * down server) — the offline flow queues instead of failing (REF-3).
 */
export function isOffline(err: unknown): boolean {
  return (
    err instanceof ApiError && (err.code === 'network' || [502, 503, 504].includes(err.status))
  );
}

const NETWORK_TEXT =
  'Keine Verbindung zum Server. Bitte prüfe das Netzwerk und versuche es erneut.';
const SERVER_TEXT = 'Der Server hat unerwartet geantwortet. Bitte versuche es erneut.';

/** tRPC error body → ApiError (German text from the server when present). */
export async function toApiError(res: Response, procedure: string): Promise<ApiError> {
  let message = SERVER_TEXT;
  let code: string | null = null;
  try {
    const body = (await res.json()) as {
      error?: { message?: string; data?: { appCode?: string | null } };
    };
    if (body.error?.message) message = body.error.message;
    code = body.error?.data?.appCode ?? null;
  } catch {
    // not JSON (proxy error page) — keep the generic text
  }
  return new ApiError(message, code, procedure, res.status);
}

/** fetch() that turns a network failure into a readable ApiError. */
async function send(url: string, init: RequestInit, procedure: string): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    throw new ApiError(NETWORK_TEXT, 'network', procedure, 0);
  }
}

export class UnauthenticatedError extends Error {
  constructor() {
    super('not_authenticated');
    this.name = 'UnauthenticatedError';
  }
}

async function call<T>(procedure: string, input: unknown, init: RequestInit = {}): Promise<T> {
  const res = await send(
    `${BASE}/${procedure}`,
    {
      method: init.method ?? 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
      body: JSON.stringify(input),
    },
    procedure,
  );
  if (res.status === 401) throw new UnauthenticatedError();
  if (!res.ok) throw await toApiError(res, procedure);
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    throw new ApiError(SERVER_TEXT, 'non_json', procedure, res.status);
  }
  const data = (await res.json()) as { result?: { data?: T }; data?: T };
  return (data.result?.data ?? data.data) as T;
}

async function query<T>(procedure: string, input: unknown): Promise<T> {
  const params = new URLSearchParams({ input: JSON.stringify(input ?? {}), batch: '0' });
  const res = await send(
    `${BASE}/${procedure}?${params.toString()}`,
    { credentials: 'include' },
    procedure,
  );
  if (res.status === 401) throw new UnauthenticatedError();
  if (!res.ok) throw await toApiError(res, procedure);
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    throw new ApiError(SERVER_TEXT, 'non_json', procedure, res.status);
  }
  const data = (await res.json()) as { result?: { data?: T } };
  return data.result?.data as T;
}

export interface RangeSession {
  id: string;
  name: string;
  status: 'draft' | 'scheduled' | 'live' | 'closed' | 'locked';
  locationId?: string;
  /**
   * Distinguishes club-event sessions (range officer signs off each
   * round) from solo training sessions (member signs off themselves).
   * Optional for back-compat — older snapshots cached before this
   * field was surfaced still load.
   */
  kind?: 'event' | 'training';
  /**
   * Reflects the current sign-off mode. Used by the referee PWA to
   * gate beta features (phone-mic shot timer) to self-signed sessions
   * only — never to anything that requires range-officer approval.
   */
  signOffState?: 'self_signed' | 'referee_signed';
}

export type ScoringMode = 'precision' | 'speed';

export interface ScenarioListRow {
  scenario: {
    id: string;
    code: string;
    titleI18n: Record<string, string>;
    descriptionMd: string;
    parSeconds: string | null;
    minShots: number;
    maxShots: number;
    scoringSchemaCode: string;
    retiredAt: string | null;
  };
  createdByName: string | null;
}

export interface CalibrationCoverage {
  hasCamera: boolean;
  calibratedTargetTypeIds: string[];
}

export interface ScenarioDetail {
  scenario: ScenarioListRow['scenario'];
  drills: Array<{
    slot: {
      id: string;
      ord: number;
      drillId: string;
      scoringMode: 'precision' | 'speed';
      distanceCm: number | null;
      nameOverride: string | null;
    };
    drillName: string;
    drillCode: string | null;
    /** Absent on servers older than the calibration marks. */
    targetTypeId?: string;
    targetName: string;
    schemaCode: string;
  }>;
}

export interface DrillRow {
  drill: {
    id: string;
    name: string;
    targetTypeId: string;
    description: string | null;
    /**
     * Suggested default distance in centimetres. NULL when the
     * drill has no canonical distance — the picker then opens
     * with no chip pre-selected.
     */
    distanceCm: number | null;
    shotCount: number;
    /**
     * Optional magazine breakdown (ordered per-mag shot counts summing
     * to shotCount). NULL = single magazine / no reload segmentation.
     */
    magazines: Array<{ shots: number }> | null;
    timeLimitS: number | null;
    locationId: string | null;
    /**
     * Recommended PAR time in seconds as a Drizzle NUMERIC string, or
     * null when the drill has no PAR. Pre-fills the referee's PAR
     * picker; the referee can override per round.
     */
    parSeconds: string | null;
    /** Recommended PAR enforcement mode. */
    parMode: 'soft' | 'hard' | 'hard_stop';
    /**
     * Optional default discipline hint (pistol / pistol_carbine / rifle).
     * Pre-fills the per-stage discipline picker; null = no default.
     */
    defaultDisciplineId: string | null;
  };
  /** Free-form identifier (lowercase, admin-editable since Phase 6). */
  schemaCode: string;
  schemaKind: 'ring_value' | 'hit_factor';
  schemaName: string;
  targetName: string;
  targetImageBlobId: string | null;
}

/** A target type the referee can pick when building a custom drill. */
export interface TargetRow {
  target: {
    id: string;
    name: string;
    defaultSchemaId: string;
    imageBlobId: string | null;
  };
  schemaCode: string;
  schemaName: string;
}

/**
 * Most-recent stage config for a session — pre-fills the custom-drill
 * wizard's "Vorheriges wiederholen" path. Mirrors
 * `participations.lastStageConfig`.
 */
export interface LastStageConfig {
  name: string;
  targetTypeId: string;
  targetName: string;
  shotCount: number;
  /** Magazine breakdown of the previous stage (or null = single mag). */
  magazines: Array<{ shots: number }> | null;
  scoringMode: ScoringMode;
  distanceCm: number;
  /** Discipline of the previous stage (or null). */
  disciplineId: string | null;
  /** Effective PAR seconds (NUMERIC string) or null. */
  effectiveParSeconds: string | null;
  effectiveParMode: 'soft' | 'hard' | 'hard_stop';
}

export interface LiveAssignment {
  assignment: {
    id: string;
    roundId: string;
    participationId: string;
    assignedRefereeUserId: string | null;
    lockedAt: string | null;
    completedAt: string | null;
    firstShotTimeMs: number | null;
    elapsedTimeMs: number | null;
    disqualifiedAt: string | null;
    dqReason: string | null;
    /** When the referee called the shooter ("Aufrufen"). Optional: older API builds omit it. */
    calledAt?: string | null;
    /** When standby (timer start announcement) was set — manually or by the SG timer. */
    standbyAt?: string | null;
  };
  round: { id: string; roundNo: number; armedAt: string | null; endedAt: string | null };
  stage: {
    id: string;
    ord: number;
    name: string;
    shotCount: number;
    /**
     * Per-round magazine breakdown (ordered per-mag shot counts summing
     * to shotCount). NULL = single magazine. Drives the keypad's reload
     * markers and the scoreboard "Nachladen" cue.
     */
    magazines: Array<{ shots: number }> | null;
    scoringSchemaId: string;
    /** Maximum time on the clock in seconds; null when the drill has
     *  no time cap. When set together with an active SG-Timer the
     *  Score screen pushes this to the device's PAR config and
     *  auto-disqualifies if the run runs over. */
    timeLimitS: number | null;
    /** Set when the stage was created from a template's scenario slot. */
    scenarioId: string | null;
  };
  /** Scenario metadata when stage.scenarioId is set; null otherwise. */
  scenarioCode: string | null;
  scenarioTitleI18n: Record<string, string> | null;
  /**
   * Effective PAR after the referee's per-round override is resolved
   * against the drill recommendation. Numeric string or null. Drives the
   * timer push (hard mode) and the scoreboard tint (soft mode).
   */
  effectiveParSeconds: string | null;
  /**
   * Effective PAR mode after override. `hard` triggers auto-DQ when
   * the SG-Timer's PAR signal fires; `soft` is visual-only.
   */
  effectiveParMode: 'soft' | 'hard' | 'hard_stop';
  /**
   * Timer safety auto-STOP in seconds (resolved server-side): 2×PAR when a
   * PAR is set, otherwise the per-session override or the club default. This
   * bounds recording (esp. variable-shot rounds with no shot cap) — it STOPS
   * the run, it does NOT disqualify. Only hard PAR auto-DQs.
   */
  maxRecordingSeconds: number | null;
  /** Output mode picked at drill instantiation time. */
  scoringMode: ScoringMode;
  /** Stage distance in centimetres. */
  distanceCm: number;
  /** Per-stage discipline code (pistol / pistol_carbine / rifle) or null. */
  disciplineCode: string | null;
  scoringSchemaCode: string;
  scoringSchemaKind: 'ring_value' | 'hit_factor';
  scoringSchemaConfig: unknown;
  /**
   * Per-zone scoring catalogue: drives the referee keypad's button
   * list and the hit indicator's ring values. Empty array if the
   * schema hasn't had its zones backfilled yet (defensive — every
   * Phase-6+ schema has at least one zone).
   */
  scoringSchemaZones: Array<{
    zoneCode: string;
    valueNumeric: number;
    ordering: number;
    displayColor: string | null;
  }>;
  /**
   * Geometric zone definitions for the stage's target type. Shape +
   * geometry payloads come from `target_zones`; the hit indicator
   * uses these to draw the actual silhouette / square / hostage
   * outline rather than synthesised concentric rings.
   */
  targetZones: Array<{
    zoneCode: string;
    ordering: number;
    shape: 'circle' | 'rect' | 'polygon';
    geometry: unknown;
    displayColor: string | null;
  }>;
  /** CV detections awaiting referee confirmation for this assignment. */
  pendingDetections: Array<{
    id: string;
    x: number;
    y: number;
    valueNumeric: number | null;
    zoneCode: string | null;
    confidence: number;
  }>;
  /** Real coordinates of shots recorded from a CV detection. */
  shotPositions: Array<{ shotId: string; x: number; y: number }>;
  /** Round-level penalties accrued for this assignment. */
  penalties: Array<{
    id: string;
    code: 'procedural' | 'miss' | 'no_shoot' | 'extra_shot';
    points: number;
    note?: string | null;
    /** Offline key (penalties.add); null for penalties from before mig 0121. */
    clientIdempotencyKey?: string | null;
  }>;
  /** Points per penalty type from the stage's scoring schema (penalties.add stores these). */
  penaltyValues?: Record<'procedural' | 'miss' | 'no_shoot' | 'extra_shot', number>;
  memberFirstName: string | null;
  memberLastName: string | null;
  memberNo: string | null;
  ownedByMe: boolean;
  shotCount: number;
  /** Live lane on the assignment. NULL on multi-lane locations before the referee claims. */
  laneId: string | null;
  /** Resolved lane name (FK → snapshot fallback). NULL when both unset. */
  laneName: string | null;
  /** Admin's default lane on the participation. Drives the claim picker's pre-selection. */
  participationLaneId: string | null;
  /** No-show (participation cancelled / withdrawn): never offered as next. Absent on older servers. */
  participationInactive?: boolean;
}

export interface LaneRow {
  id: string;
  locationId: string;
  name: string;
  sortOrd: number;
  createdAt: string;
}

export interface GeneratedRound {
  round: { id: string; roundNo: number; stageId: string };
  assignments: { id: string }[];
}

/**
 * Lowest-ord planned stage in a session. The referee picker pins this
 * with Start / Skip / Replace actions; null means no plan remains.
 */
export interface PlannedStageInfo {
  stage: {
    id: string;
    ord: number;
    name: string;
    distanceCm: number;
    shotCount: number;
    timeLimitS: number | null;
    scoringMode: ScoringMode;
    drillId: string | null;
    /** Per-stage discipline set during planning (or null = not yet chosen). */
    disciplineId: string | null;
    status: 'planned' | 'active' | 'skipped';
  };
  drillName: string | null;
  drillDescription: string | null;
  drillArchivedAt: string | null;
}

export interface LoginMode {
  trusted: boolean;
  users?: { id: string; displayName: string; hasPin: boolean }[];
}

/**
 * Strictly increasing `timer.tick` sequence number. Ticks are sent
 * fire-and-forget and can overtake each other on the network; the server
 * drops one older than the last it accepted from this sender
 * (`timerTickSource`, new per page load, so counters of different
 * devices are never compared).
 */
const timerTickSource =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
let timerTickSeq = Date.now();
function nextTimerTickSeq(): number {
  timerTickSeq = Math.max(timerTickSeq + 1, Date.now());
  return timerTickSeq;
}

/** One "Fehler merken" press as the server lists it (bugReports.mine). */
export interface BugReportRow {
  id: string;
  sessionId: string;
  sessionName: string;
  sessionStatus: RangeSession['status'];
  reportedAt: string;
  screen: string | null;
  status: 'open' | 'described' | 'dismissed';
  description: string | null;
}

export const api = {
  /** "Fehler merken": store a bug marker (idempotent by id; offline queue in lib/bugReports). */
  createBugReport: (input: {
    id: string;
    sessionId: string;
    surface: 'referee';
    deviceId: string;
    reportedAt: string;
    screen: string | null;
    context: Record<string, unknown>;
    traceId: string | null;
    reporterUserId: string | null;
  }) => call<{ ok: true }>('bugReports.create', input),
  /** The signed-in user's id (bug-report queue owner). */
  myUserId: () =>
    query<{ userId?: string }>('me.profile', undefined).then((p) => p.userId ?? null),
  /** My open bug markers (described / dismissed ones drop out). */
  myBugReports: () => query<BugReportRow[]>('bugReports.mine', {}),
  describeBugReport: (id: string, description: string) =>
    call<{ ok: true }>('bugReports.describe', { id, description }),
  dismissBugReport: (id: string) => call<{ ok: true }>('bugReports.dismiss', { id }),

  /**
   * Public — returns trust state + dropdown candidate list when the
   * caller's IP matches an enabled trusted-network CIDR. Used by the
   * sign-in screen to decide whether to render dropdown+PIN or the
   * classic email+password+TOTP form.
   */
  loginMode: () => query<LoginMode>('auth.loginMode', undefined),
  /** Public — club wording settings (gender style of person terms). */
  wording: () => query<{ genderStyle: 'neutral' | 'masculine' }>('public.wording', undefined),
  /**
   * Step 1 of self-service PIN reset: server hashes the new PIN, sends
   * a 6-digit code via SMS to the user's linked phone (if any), and
   * parks the candidate hash in pin_reset_requests. Always returns ok
   * to avoid leaking which identifiers exist.
   */
  requestPinReset: (identifier: string, newPin: string) =>
    call<{ ok: true }>('auth.requestPinReset', { identifier, newPin }),
  /** Step 2: submit the 6-digit code; on match, the candidate PIN is applied. */
  confirmPinReset: (identifier: string, code: string) =>
    call<{ ok: true }>('auth.confirmPinReset', { identifier, code }),
  // Role-scoped picker list: officers see all sessions, a plain member
  // sees only the trainings they created (so a member scoring their own
  // self-started training isn't shown everyone else's sessions).
  listSessions: () => query<RangeSession[]>('sessions.listForReferee', undefined),
  /** Role names of the signed-in user (gates "Training starten"; server re-checks). */
  myRoles: () =>
    query<{ roles?: string[] }>('me.profile', undefined).then((p) => p.roles ?? []),
  liveAssignments: (sessionId: string) =>
    query<LiveAssignment[]>('sessions.liveAssignments', { sessionId }),
  claim: (assignmentId: string, laneId?: string | null) =>
    call<LiveAssignment['assignment']>('sessions.claimRoundAssignment', {
      assignmentId,
      ...(laneId !== undefined ? { laneId } : {}),
    }),
  /** Randomise the not-yet-started shooters of a running round. */
  shuffleRoundOrder: (roundId: string) =>
    call<{ moved: number }>('sessions.shuffleRoundOrder', { roundId }),
  /**
   * "Schütze:in aufrufen" — claims the assignment (same guards + return
   * shape as `claim`) and stamps `called_at`, clearing any old standby.
   * Calling IS claiming in the 3-step flow (Bestätigen → Aufrufen →
   * Standby); `claim` stays for legacy callers only.
   */
  callShooter: (assignmentId: string, laneId?: string | null) =>
    tracked('Schütze:in aufrufen', { assignmentId, laneId: laneId ?? null, expect: 'board shows the called shooter' }, () =>
      call<LiveAssignment['assignment']>('sessions.callShooter', {
        assignmentId,
        ...(laneId !== undefined ? { laneId } : {}),
      }),
    ),
  /**
   * Undo of `callShooter` (roster "Rückgängig"): releases this referee's
   * call while the shooter hasn't begun. Fails with `call_not_yours` /
   * `shooter_already_started` otherwise.
   */
  uncallShooter: (assignmentId: string) =>
    call<{ assignmentId: string }>('sessions.uncallShooter', { assignmentId }),
  /**
   * Standby (= "timer start" announcement) on/off for a called,
   * not-yet-completed assignment. A paired SG timer arming/starting sets
   * it server-side automatically via timer.tick.
   */
  setStandby: (assignmentId: string, standby: boolean) =>
    call<unknown>('sessions.setStandby', { assignmentId, standby }),
  moveAssignmentToLane: (assignmentId: string, laneId: string | null) =>
    call<LiveAssignment['assignment']>('sessions.moveAssignmentToLane', {
      assignmentId,
      laneId,
    }),
  lanesForLocation: (locationId: string) => query<LaneRow[]>('locations.lanesList', { locationId }),
  generateRound: (stageId: string, roundNo: number) =>
    call<GeneratedRound>('participations.generateRound', { stageId, roundNo }),
  drills: (locationId?: string | null) =>
    query<DrillRow[]>('drills.list', { locationId: locationId ?? null, includeArchived: false }),
  /** Target types for the custom-drill builder. */
  targets: () => query<TargetRow[]>('targets.list', undefined),
  /** Previous-stage config for the "Vorheriges wiederholen" pre-fill. */
  lastStageConfig: (sessionId: string) =>
    query<LastStageConfig | null>('participations.lastStageConfig', { sessionId }),
  /**
   * Instantiate + start a custom one-off drill (no catalog pick). Mirrors
   * `startDrillRound`'s return shape. `saveAsDraft` also persists a draft
   * catalog drill for later admin review.
   */
  startCustomRound: (input: {
    sessionId: string;
    name: string;
    targetTypeId: string;
    shotCount: number;
    magazines?: Array<{ shots: number }> | null;
    disciplineId: string;
    mode: ScoringMode;
    distanceCm: number;
    parSecondsOverride?: number | null;
    parModeOverride?: 'soft' | 'hard' | 'hard_stop' | null;
    /** "Kein PAR": the round runs without any PAR (null override = drill's PAR). */
    parDisabled?: boolean;
    saveAsDraft: boolean;
  }) =>
    call<{
      stage: { id: string; name: string };
      round: { id: string };
      assignments: { id: string }[];
    }>('participations.startCustomRound', input),
  /** The discipline catalogue (pistol / pistol_carbine / rifle). */
  disciplines: () =>
    query<Array<{ id: string; code: string; nameI18nKey: string }>>('disciplines.list', {}),
  /**
   * Which targets have a camera calibration at this range. `hasCamera`
   * false → show no marks (every target would be "uncalibrated").
   */
  calibrationCoverage: (locationId: string) =>
    query<CalibrationCoverage>('vision.calibrationCoverage', { locationId }),
  scenarios: () => query<ScenarioListRow[]>('scenarios.list', { includeRetired: false }),
  scenarioById: (id: string) => query<ScenarioDetail | null>('scenarios.byId', { id }),
  expandScenarioToSession: (sessionId: string, scenarioId: string) =>
    call<{ plannedStageIds: string[] }>('scenarios.expandToSession', { sessionId, scenarioId }),
  startDrillRound: (
    sessionId: string,
    drillId: string,
    disciplineId: string,
    mode: ScoringMode,
    distanceCm: number,
    parOverride?: {
      parSecondsOverride: number | null;
      parModeOverride: 'soft' | 'hard' | 'hard_stop' | null;
      /** "Kein PAR": the round runs without any PAR. Omitted otherwise. */
      parDisabled?: boolean;
    },
  ) =>
    call<{
      stage: { id: string; name: string };
      round: { id: string };
      assignments: { id: string }[];
    }>('participations.startDrillRound', {
      sessionId,
      drillId,
      disciplineId,
      mode,
      distanceCm,
      ...(parOverride ?? {}),
    }),
  nextRound: (stageId: string) =>
    call<{
      stage: { id: string; name: string };
      round: { id: string };
      assignments: { id: string }[];
    }>('participations.nextRound', { stageId }),
  /**
   * Arm a round — flips `rounds.armed_at` to NOW() so vision detection
   * spins up against the round's target. Idempotent (server uses
   * COALESCE). Called on tap of "Runde scharfschalten" or auto-fired
   * when the SG-Timer is started without a prior arm.
   */
  armRound: (roundId: string) =>
    call<{ ok: true; armedAt: string | null }>('participations.armRound', { roundId }),
  nextPlannedStage: (sessionId: string) =>
    query<PlannedStageInfo | null>('participations.nextPlannedStage', { sessionId }),
  startPlannedStage: (stageId: string, disciplineId?: string) =>
    call<{
      stage: { id: string; name: string };
      round: { id: string };
      assignments: { id: string }[];
    }>('participations.startPlannedStage', { stageId, ...(disciplineId ? { disciplineId } : {}) }),
  skipPlannedStage: (stageId: string) =>
    call<{ stage: { id: string } }>('participations.skipPlannedStage', { stageId }),
  replacePlannedStage: (
    stageId: string,
    drillId: string,
    scoringMode: ScoringMode,
    distanceCm?: number,
    parOverride?: {
      parSecondsOverride: number | null;
      parModeOverride: 'soft' | 'hard' | 'hard_stop' | null;
      /** "Kein PAR": the round runs without any PAR. Omitted otherwise. */
      parDisabled?: boolean;
    },
    disciplineId?: string,
  ) =>
    call<{ stage: { id: string } }>('participations.replacePlannedStage', {
      stageId,
      drillId,
      scoringMode,
      ...(distanceCm != null ? { distanceCm } : {}),
      ...(parOverride ?? {}),
      ...(disciplineId ? { disciplineId } : {}),
    }),
  /**
   * Active users who may sign a Badeschluss (admin / range_officer).
   * Drives the signer dropdowns in the two-signature dialog.
   */
  eligibleSigners: () =>
    query<{ id: string; displayName: string; functionLabel: string }[]>(
      'sessions.eligibleSigners',
      undefined,
    ),
  /**
   * Upload a drawn signature PNG (as a data URL) to the shared blob
   * store and return its blob id. Hits the same-origin
   * `/api/blobs/upload` route (proxied to apps/web by nginx).
   */
  uploadSignature: async (dataUrl: string): Promise<string> => {
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], 'signature.png', { type: 'image/png' });
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/blobs/upload', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (res.status === 401) throw new UnauthenticatedError();
    if (!res.ok) throw new Error(`Upload fehlgeschlagen (${res.status})`);
    const { id } = (await res.json()) as { id?: string };
    if (!id) throw new Error('Upload ohne Blob-ID');
    return id;
  },
  /**
   * "Training starten": draft/scheduled → live (scoreboard + Badeschluss).
   * Optional — the first round start sets the training live on its own.
   * Fails with `session_not_draft` when it is already live.
   */
  startSession: (id: string) => call<unknown>('sessions.start', { id }).then(() => undefined),
  /** Started but unconfirmed rounds — shown before Badeschluss (REF-5). */
  closeCheck: (id: string) => query<{ openRounds: number }>('sessions.closeCheck', { id }),
  closeSession: (
    id: string,
    signatures: { userId: string; signatureBlobId: string }[],
    force = false,
  ) => call<{ id: string }>('sessions.close', { id, signatures, ...(force ? { force } : {}) }),
  addShot: (
    payload: {
      roundAssignmentId: string;
      ord: number;
      valueNumeric: number;
      clientIdempotencyKey: string;
    },
    idempotencyKey: string,
  ) =>
    tracked(
      'Schuss erfassen',
      { roundAssignmentId: payload.roundAssignmentId, ord: payload.ord, value: payload.valueNumeric },
      () =>
        call('shots.add', payload, {
          headers: { 'Idempotency-Key': idempotencyKey },
        }),
    ),
  updateShot: (payload: {
    roundAssignmentId: string;
    clientIdempotencyKey: string;
    valueNumeric?: number | null;
    zoneCode?: 'A' | 'C' | 'D' | 'M' | 'NS' | null;
    note?: string;
  }) => call('shots.update', payload),
  removeShot: (payload: { roundAssignmentId: string; clientIdempotencyKey: string }) =>
    call<{ ok: true }>('shots.remove', payload),
  setRoundTiming: (payload: {
    roundAssignmentId: string;
    firstShotTimeMs?: number;
    elapsedTimeMs?: number;
  }) => call<{ updated: number }>('shots.setRoundTiming', payload),
  confirmRound: (roundAssignmentId: string) =>
    tracked('Runde bestätigen', { roundAssignmentId, expect: 'round confirmed, board shows result' }, () =>
      call('shots.confirmRound', { roundAssignmentId }),
    ),
  /** Server truth of one assignment's penalties (offline reconcile), oldest first. */
  penaltiesByAssignment: (assignmentId: string) =>
    query<
      Array<{
        id: string;
        code: 'procedural' | 'miss' | 'no_shoot' | 'extra_shot';
        points: number;
        note: string | null;
        clientIdempotencyKey: string | null;
      }>
    >('penalties.listByAssignment', { assignmentId }),
  /**
   * Remove a penalty by its server id — only for penalties recorded before
   * mig 0121 (no clientIdempotencyKey, so the outbox can't address them).
   */
  removePenaltyById: (roundAssignmentId: string, id: string) =>
    call<{ ok: true }>('penalties.remove', { roundAssignmentId, id }),
  /**
   * Capture (or recapture) a "before" / "after" snapshot for the kiosk
   * slot-B blink widget. The auto-triggers on claim + last-shot run
   * server-side; this is the manual "Vorher-Bild neu aufnehmen" tap
   * the referee uses while the shooter is still preparing. The server
   * rejects `force=true` once the round timer has started or any shot
   * has been logged.
   */
  captureRoundSnapshot: (payload: {
    roundAssignmentId: string;
    kind: 'before' | 'after';
    force?: boolean;
  }) =>
    call<{ snapshotId: string | null; capturedAt: string | null; alreadyExisted: boolean }>(
      'snapshots.capture',
      payload,
    ),
  disqualifyRound: (roundAssignmentId: string, reason: string, opts?: { terminal?: boolean }) =>
    tracked('Runde disqualifizieren', { roundAssignmentId, reason, terminal: opts?.terminal ?? false }, () =>
      call('shots.disqualifyRound', { roundAssignmentId, reason, terminal: opts?.terminal ?? false }),
    ),
  /**
   * Push a transient SG-Timer update so the spectator board can render
   * a live "current run" strip. Best-effort — failures are silently
   * dropped (the round itself is durable via setRoundTiming + shots).
   */
  timerTick: (payload: {
    rangeSessionId: string;
    roundAssignmentId: string;
    shooterLabel: string;
    state: 'armed' | 'running' | 'stopped' | 'idle';
    /** Wall-clock (ms since epoch) when the start beep fired; null
     *  while armed. Lets the board derive a countdown without polling. */
    startedAt: number | null;
    firstShotTimeMs: number | null;
    lastShotTimeMs: number | null;
    shotCount: number;
    expectedShots: number | null;
    /** This device's clock when the tick was sent — the server uses it
     *  to move `startedAt` onto its own clock. */
    sentAt: number;
    /** Every shot's time from the start beep, in shot order. */
    shotTimesMs: number[];
  }) =>
    call<{ ok: true; stale?: true }>('timer.tick', {
      ...payload,
      seq: nextTimerTickSeq(),
      seqSource: timerTickSource,
    }),
  /**
   * Can the camera score this shooter? Drives the small "Trefferanzeige"
   * banner in the Score screen: missing calibration for the stage's
   * target, camera or vision service failing — with what to do about it
   * (`message`). `no_camera` / `detecting` show nothing.
   */
  visionStatus: (rangeSessionId: string, roundAssignmentId?: string | null) =>
    query<VisionSessionStatus>('vision.statusForSession', {
      rangeSessionId,
      ...(roundAssignmentId ? { roundAssignmentId } : {}),
    }),
  /**
   * Referee accepts a CV detection — server-side companion to the
   * local Dexie shot write. Inserts the shot + sets
   * `vision_detections.consumed_shot_id`.
   */
  confirmDetection: (payload: {
    detectionId: string;
    ord: number;
    valueNumeric?: number | null;
    zoneCode?: 'A' | 'C' | 'D' | 'M' | 'NS' | null;
    clientIdempotencyKey: string;
  }) => call('vision.confirmDetection', payload),
  /** Referee rejects a CV detection — sets `dismissed_at`. */
  dismissDetection: (detectionId: string) => call('vision.dismissDetection', { detectionId }),
  /**
   * Resolved per-shot hit positions for one assignment. Prefers
   * `shot_positions` (the post-migration 0057 source of truth);
   * falls back to consumed `vision_detections` for legacy data.
   * Drives the new snapshot-comparison modal's Analyse tab.
   */
  shotPositionsForAssignment: (roundAssignmentId: string) =>
    query<ShotPositionRow[]>('shotPositions.listForAssignment', { roundAssignmentId }),
  /**
   * Before/after snapshot IDs for an assignment. Used by the
   * snapshot-comparison modal to load Vorher/Nachher images via the
   * existing `/api/round-snapshots/<id>` route.
   */
  snapshotPairForAssignment: (roundAssignmentId: string) =>
    query<{
      roundAssignmentId: string;
      before: { id: string; capturedAt: string } | null;
      after: { id: string; capturedAt: string } | null;
      status: 'idle' | 'before_only' | 'pair_ready';
    }>('snapshots.listByAssignment', { roundAssignmentId }),
};

export interface ShotPositionRow {
  shotId: string;
  ord: number;
  xNorm: number;
  yNorm: number;
  targetXNorm: number;
  targetYNorm: number;
  /**
   * `shot_positions.analyzed_blob_id` — pre-rendered after-frame
   * with red hole markers baked in. NULL for legacy rows; the modal
   * falls back to compositing markers client-side on the raw after
   * image in that case.
   */
  analyzedBlobId: string | null;
  /** ECC alignment translation exceeded the runtime cap. */
  drift: boolean;
  estimated?: boolean;
  source: 'shot_positions' | 'legacy_vision_detection';
}

export interface VisionSessionStatus {
  state:
    | 'no_session'
    | 'no_active_round'
    | 'no_camera'
    | 'no_target'
    | 'needs_calibration'
    | 'vision_unreachable'
    | 'camera_error'
    | 'detecting';
  targetTypeId: string | null;
  targetName: string | null;
  calibrationId: string | null;
  lastDetectionAt: string | null;
  /** German banner text for every state that needs attention. */
  message: string | null;
}

/**
 * Fold the call/claim result's lock + call/standby stamps into a cached
 * roster row, so the Score screen shows "Aufgerufen" immediately instead
 * of waiting for the next liveAssignments refetch. Defensive: the server
 * returns the raw assignment row, older builds may omit the new fields.
 */
export function applyCallResult(
  a: LiveAssignment,
  res:
    | (Partial<LiveAssignment['assignment']> & {
        laneId?: string | null;
        laneNameSnapshot?: string | null;
      })
    | null
    | undefined,
): LiveAssignment {
  if (!res || typeof res !== 'object') return a;
  return {
    ...a,
    // The claim resolves the lane (picked, kept or the participation's) —
    // keep it, an offline "Aufrufen" reuses it for the next shooter.
    ...(res.laneId !== undefined ? { laneId: res.laneId } : {}),
    ...(res.laneNameSnapshot !== undefined ? { laneName: res.laneNameSnapshot } : {}),
    assignment: {
      ...a.assignment,
      lockedAt: res.lockedAt ?? a.assignment.lockedAt,
      calledAt: res.calledAt ?? a.assignment.calledAt ?? new Date().toISOString(),
      standbyAt: res.standbyAt !== undefined ? res.standbyAt : null,
    },
  };
}
